import { useDealership } from '@/contexts/DealershipContext'
import { apiClient } from '@/services/api'
import { Dealership } from '@/types'
import { Card, Divider, Group, Loader, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconCalendarOff, IconCar, IconCheck, IconClock, IconX } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'

interface StatusCounts {
  pending: number
  inProgress: number
  completed: number
  abandoned: number
  total: number
}

interface RecoveryStatusData {
  missedAppointment: StatusCounts
  showroomUnsold: StatusCounts
}

const defaultCounts: StatusCounts = {
  pending: 0,
  inProgress: 0,
  completed: 0,
  abandoned: 0,
  total: 0,
}

export function RecoveryStatusCard() {
  const { selectedEntity } = useDealership()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [data, setData] = useState<RecoveryStatusData>({
    missedAppointment: defaultCounts,
    showroomUnsold: defaultCounts,
  })

  useEffect(() => {
    if (selectedEntity && selectedEntity.type === 'dealership') {
      fetchStatus()
      const interval = setInterval(fetchStatus, 60000)
      return () => clearInterval(interval)
    }
  }, [selectedEntity])

  const fetchStatus = async () => {
    if (!selectedEntity || selectedEntity.type !== 'dealership') return

    setLoading(true)
    setLoadError(null)
    try {
      const dealership = selectedEntity as Dealership
      const response = await apiClient.get<
        { success: boolean; data?: RecoveryStatusData; error?: string } | RecoveryStatusData
      >(`/dealerships/${dealership.id}/recovery-status`)

      const payload =
        response && typeof response === 'object' && 'success' in response && response.success && response.data
          ? response.data
          : response && typeof response === 'object' && 'missedAppointment' in response
            ? (response as RecoveryStatusData)
            : null

      if (payload) {
        setData({
          missedAppointment: payload.missedAppointment || defaultCounts,
          showroomUnsold: payload.showroomUnsold || defaultCounts,
        })
      } else if (response && typeof response === 'object' && 'success' in response && !response.success) {
        setLoadError((response as { error?: string }).error || 'Could not load recovery status')
      }
    } catch (error) {
      console.error('Failed to fetch recovery status:', error)
      setLoadError('Could not load recovery status')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="center" py="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading recovery status...
          </Text>
        </Group>
      </Card>
    )
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Text fw={600} size="lg">
          Recovery Status
        </Text>
        {loadError && (
          <Text size="sm" c="red">
            {loadError}
          </Text>
        )}

        {/* Missed Appointment Recovery */}
        <RecoverySection
          title="Missed Appointments"
          icon={<IconCalendarOff size={18} />}
          color="blue"
          counts={data.missedAppointment}
        />

        <Divider />

        {/* Showroom Unsold Recovery */}
        <RecoverySection
          title="Showroom Unsold Follow-up"
          icon={<IconCar size={18} />}
          color="violet"
          counts={data.showroomUnsold}
        />
      </Stack>
    </Card>
  )
}

interface RecoverySectionProps {
  title: string
  icon: React.ReactNode
  color: string
  counts: StatusCounts
}

function RecoverySection({ title, icon, color, counts }: RecoverySectionProps) {
  const activeCount = counts.pending + counts.inProgress

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <ThemeIcon size="sm" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Text fw={500} size="sm">
          {title}
        </Text>
        {activeCount > 0 && (
          <Text size="xs" c={color}>
            ({activeCount} active)
          </Text>
        )}
      </Group>

      <SimpleGrid cols={4}>
        <StatusItem icon={<IconClock size={16} />} color="yellow" label="Pending" value={counts.pending} />
        <StatusItem icon={<IconCalendarOff size={16} />} color="blue" label="In Progress" value={counts.inProgress} />
        <StatusItem icon={<IconCheck size={16} />} color="green" label="Completed" value={counts.completed} />
        <StatusItem icon={<IconX size={16} />} color="gray" label="Stopped" value={counts.abandoned} />
      </SimpleGrid>
    </Stack>
  )
}

interface StatusItemProps {
  icon: React.ReactNode
  color: string
  label: string
  value: number
}

function StatusItem({ icon, color, label, value }: StatusItemProps) {
  return (
    <Stack gap={2} align="center">
      <ThemeIcon size="md" radius="md" variant="light" color={color}>
        {icon}
      </ThemeIcon>
      <Text size="lg" fw={700}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Stack>
  )
}
